import { Results } from '../../models/unit.model';
import type { ProductMaintenance } from '../../models/product-characteristics.model';
import { isMaintenanceElement } from '../element.helpers';
import { ElementPath, isElement } from '../recursive-element.helpers';
import { getConversionFactorValue } from '../conversion-factors.helpers';
import { roundToDecimals } from '../math.helpers';
import { IVL_B_PHASE_REFERENCE_PERIOD_YEARS } from '../lifecycles.data';
import { getCharacteristicsSbefCode } from '../sbef.helpers';
import { getElementArea } from '../element-quantity-orientation.helpers';
import { getGenericProductCharacteristics } from './generic-product-characteristics.helpers';
import { MapResultsContext } from './map-results-context.model';

/**
 * True when {@link productElement} has a Maintenance category wrapper ancestor.
 * Legacy schablon applies in that case; IVL B2 from product characteristics is skipped.
 */
export const isUnderMaintenanceElement = (path: ElementPath): boolean =>
  path.some((el) => isElement(el) && isMaintenanceElement(el));

/**
 * Number of maintenance actions during the reference period.
 * IVL: round(50 / interval - 1, 1), never negative.
 */
export const getMaintenanceActionCount = (interval: number): number => {
  if (!interval) return 0;
  return Math.max(
    roundToDecimals(IVL_B_PHASE_REFERENCE_PERIOD_YEARS / interval - 1, 1),
    0,
  );
};

/**
 * Resolve the base value a B2 maintenance {@link ProductMaintenance} rule scales:
 * the A-stage CO2e for `'co2e_A'` rules, otherwise the product's own quantity
 * results. Facade products (timber, bricks, sheet metal) carry no m² conversion
 * factor — the IVL key figures are per m² of the element — so m² rules fall back
 * to the closest parent element's area.
 */
const getMaintenanceBaseValue = (
  rule: ProductMaintenance,
  results: Results,
  path: ElementPath,
): number => {
  if (rule.units === 'co2e_A') {
    return getConversionFactorValue(results, 'co2e_A');
  }
  const fromResults = getConversionFactorValue(results, ...rule.units);
  const hasQuantityInResults = rule.units.some((unit) => unit in results);
  if (hasQuantityInResults || !rule.units.includes('m²')) return fromResults;

  const parentElement = path.toReversed().find(isElement);
  return parentElement ? getElementArea(parentElement) : 0;
};

/**
 * Add B2 maintenance emissions based on the product's `characteristics.maintenance`
 * rule for the element's SBEF code (IVL, see DEV-3322).
 *
 * Runs after `mapResultsByElementCategories` in `mapResults`. Skipped when the product
 * sits under a Maintenance category wrapper — legacy schablon owns B2 there.
 *
 * Only products with a rule matching the SBEF code contribute. Climate-improved
 * products are excluded by not having any maintenance characteristics. A rule with
 * factor 0 actively clears any existing B2 for that product (e.g. sheet metal roofs).
 */
export const mapResultsByMaintenance = (
  results: Results,
  { path, productElement }: MapResultsContext,
  /** Pre-category A-stage values — legacy Maintenance strips A keys before this runs */
  sourceResults: Results = results,
): Results => {
  if (isUnderMaintenanceElement(path)) return results;

  const maintenance = getGenericProductCharacteristics(
    path,
    productElement,
  )?.maintenance;
  if (!maintenance) return results;

  // Keyed by the element SBEF code or, when unset, the product's sole maintenance
  // SBEF key (e.g. windows keyed '55') so it resolves even without a picked code
  const sbefCode = getCharacteristicsSbefCode(
    productElement,
    path,
    maintenance,
  );
  const rule = sbefCode ? maintenance[sbefCode] : undefined;
  if (!rule) return results;

  // A1-A5 rows scale the A-stage result, quantity rows scale kg CO2e per unit
  const base = getMaintenanceBaseValue(rule, sourceResults, path);

  return {
    ...results,
    co2e_B2: base * rule.factor * getMaintenanceActionCount(rule.interval),
  };
};
